import { useCallback, useEffect } from 'react';
import { useSetImmerAtom } from 'jotai-immer';
import { editStateAtom, editModelAtom } from '../provider_edit_atoms';
import { editStateVM, modelVM } from '../provider_edit_view_models';
import { ValidOption } from '../../../../core/components/validation/types';

// provider imports
import providerRepo from '../../data/providerRepo';
import { useProviderEditStore } from '../pagehooks/useProviderEditStore';
import { AdminProviderModel } from '../provider_edit_types';

export const useProviderEditFormInit = (): void => {
    const { state: pageState } = useProviderEditStore();
    const setState = useSetImmerAtom(editStateAtom);
    const setModel = useSetImmerAtom(editModelAtom);

    const getModel = useCallback(
        async (id: string): Promise<void> => {
            try {
                const detail = (await providerRepo.getDetail(id)) as AdminProviderModel;
                const taxonomy = await providerRepo.getTaxonomyList();

                const model = modelVM(detail);
                const withTaxonomy = {
                    ...model,
                    PrimaryTaxonomy: {
                        ...model.PrimaryTaxonomy,
                        options: convertFacilitiesToValidOptions(taxonomy),
                    },
                };

                setModel(() => withTaxonomy);
                setState(draft => {
                    draft.init = false;
                });
            } catch (err: any) {
                // handled upstream, need a catch when using a try/finally
            } finally {
                setState(draft => {
                    draft.waiting = false;
                });
            }
        },
        [setModel, setState],
    );

    useEffect(() => {
        setState(draft => {
            if (draft.init) {
                draft.waiting = true;
                getModel(pageState.id);
            }
            return draft;
        });

        // destroy atoms
        return (): void => {
            setState(() => editStateVM());
            setModel(() => modelVM());
        };
    }, [pageState.mode, setState, setModel, pageState.id, getModel]);
};

const convertFacilitiesToValidOptions = (facilities: { Id: string; TaxonomyDesc: string }[]): ValidOption[] => {
    const fac = [{ lbl: 'Choose Primary Taxonomy', val: '' }]; // default value
    facilities.forEach(facility => {
        fac.push({
            lbl: facility.TaxonomyDesc,
            val: facility.Id,
        });
    });
    return fac;
};
